import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Contact() {
  const { t } = useTranslation('home');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    inquiry: 'music',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const sectionAnim = useScrollAnimation();

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } }
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new FormData(formElement);
      
      const response = await fetch('https://readdy.ai/api/form/d5m37gjlpot0rgnb7kgg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSend as any).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', inquiry: 'music', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-[#F5F5DC] to-[#E8E8D0]">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div 
          ref={sectionAnim.ref}
          initial="hidden"
          animate={sectionAnim.isVisible ? "visible" : "hidden"}
          variants={fadeInUp}
          className="text-center mb-16"
        >
          <h2 className="font-serif text-5xl text-[#1B3A5F] mb-4">
            {t('contact.title')}
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-transparent via-[#C5B358] to-transparent mx-auto mb-6"></div>
          <p className="text-lg text-[#1B3A5F]/80 max-w-2xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <div className="h-px bg-gradient-to-r from-transparent via-[#C5B358] to-transparent mb-16"></div>

        <div className="grid md:grid-cols-2 gap-12">
          <motion.div
            initial="hidden"
            animate={sectionAnim.isVisible ? "visible" : "hidden"}
            variants={fadeInLeft}
          >
            <div className="bg-[#fefdfb] rounded-lg p-8 shadow-lg border-2 border-[#C5B358]">
              <form 
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit} 
                className="bg-white/60 backdrop-blur-sm rounded-lg shadow-xl p-8 border border-[#C5B358]/20"
              >
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-[#1B3A5F] mb-2">
                      {t('contact.form.name')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-md border border-[#1B3A5F]/20 focus:border-[#8B1538] focus:ring-2 focus:ring-[#8B1538]/20 outline-none transition-all bg-white/80"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-[#1B3A5F] mb-2">
                      {t('contact.form.email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-md border border-[#1B3A5F]/20 focus:border-[#8B1538] focus:ring-2 focus:ring-[#8B1538]/20 outline-none transition-all bg-white/80"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="inquiry" className="block text-sm font-medium text-[#1B3A5F] mb-2">
                    {t('contact.form.inquiry')}
                  </label>
                  <select
                    id="inquiry"
                    name="inquiry"
                    value={formData.inquiry}
                    onChange={(e) => setFormData({ ...formData, inquiry: e.target.value })}
                    className="w-full px-4 py-3 rounded-md border border-[#1B3A5F]/20 focus:border-[#8B1538] focus:ring-2 focus:ring-[#8B1538]/20 outline-none transition-all bg-white/80 cursor-pointer"
                  >
                    <option value="music">{t('contact.form.music')}</option>
                    <option value="consulting">{t('contact.form.consulting')}</option>
                    <option value="both">{t('contact.form.both')}</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-[#1B3A5F] mb-2">
                    {t('contact.form.message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 rounded-md border border-[#1B3A5F]/20 focus:border-[#8B1538] focus:ring-2 focus:ring-[#8B1538]/20 outline-none transition-all resize-none bg-white/80"
                  ></textarea>
                  <p className="text-sm text-[#1B3A5F]/60 mt-1">
                    {formData.message.length}/500 {t('contact.form.characters')}
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-green-800 text-center font-medium">
                      {t('contact.form.success')}
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-800 text-center font-medium">
                      {t('contact.form.error')}
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#8B1538] to-[#6B0F2A] text-white py-4 rounded-md font-serif text-lg hover:shadow-lg transition-all duration-300 border-2 border-[#C5B358] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
                </button>
              </form>
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            animate={sectionAnim.isVisible ? "visible" : "hidden"}
            variants={fadeInRight}
            className="space-y-8"
          >
            <div className="bg-gradient-to-br from-[#1e3a5f] to-[#2a4a6f] rounded-lg p-8 text-white shadow-lg">
            </div>

            <div className="bg-[#fefdfb] rounded-lg p-8 shadow-lg border border-[#C5B358]">
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
